import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/base/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { castMembers } from "@/mocks/cast";

export default function CastPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = castMembers.find((m) => m.id === selectedId);

  useSeo({
    title: "善良的小姨子-演员介绍",
    description:
      "善良的小姨子演员及角色介绍：主要演员照片、姓名、扮演角色及演员简介、代表作品一览，帮助你了解剧中人物背后的表演者。",
    keywords: "善良的小姨子演员,善良的小姨子角色介绍,善良的小姨子主演,韩志旼,李到晛",
  });

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">演员介绍</span>
        </nav>

        <SectionHeading
          eyebrow="主创阵容"
          title="演员及角色介绍"
          desc="点击任意演员卡片，查看演员简介、代表作品与角色形象说明。"
        />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
          {castMembers.map((member) => (
            <button
              key={member.id}
              type="button"
              onClick={() => setSelectedId(member.id)}
              className="group text-left rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="relative w-full aspect-square overflow-hidden bg-background-200">
                <img
                  src={member.photo}
                  alt={`善良的小姨子 演员 ${member.name} 饰演 ${member.role}`}
                  title={`${member.name} 饰 ${member.role}`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/40 transition-colors flex items-center justify-center">
                  <span className="px-3 py-1.5 rounded-md bg-background-50 text-foreground-900 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    查看详情
                  </span>
                </span>
              </div>
              <div className="p-3">
                <p className="font-heading text-sm font-bold text-foreground-950 truncate">{member.name}</p>
                <p className="text-xs text-primary-600 mt-1 truncate">饰 {member.role}</p>
                <p className="text-[11px] text-foreground-400 mt-0.5 truncate">{member.roleType}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {selected && (
        <div
          className="fixed inset-0 z-[60] bg-foreground-950/60 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setSelectedId(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-2xl bg-background-50 rounded-lg overflow-hidden max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setSelectedId(null)}
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-background-50/90 text-foreground-800 flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <div className="flex flex-col sm:flex-row">
                <div className="w-full sm:w-56 shrink-0 aspect-square sm:aspect-auto sm:h-full bg-background-200">
                  <img
                    src={selected.photo}
                    alt={`演员 ${selected.name}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5 md:p-6 flex-1">
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{selected.name}</h3>
                  <p className="text-primary-600 text-sm mt-1">
                    饰演 {selected.role}
                    <span className="text-foreground-400 ml-2 text-xs">{selected.roleType}</span>
                  </p>
                  <div className="mt-4">
                    <h4 className="text-xs font-medium text-foreground-500 mb-1.5">演员简介</h4>
                    <p className="text-sm text-foreground-700 leading-relaxed">{selected.bio}</p>
                  </div>
                  <div className="mt-4">
                    <h4 className="text-xs font-medium text-foreground-500 mb-1.5">代表作品</h4>
                    <p className="text-sm text-foreground-700 leading-relaxed">{selected.works}</p>
                  </div>
                  <div className="mt-4 rounded-md bg-secondary-50 border border-secondary-200 p-3">
                    <h4 className="text-xs font-medium text-secondary-800 mb-1.5">角色形象与剧中表现</h4>
                    <p className="text-sm text-secondary-900 leading-relaxed">{selected.character}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-5 md:px-6 py-4 border-t border-background-200 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 返回演员列表
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}